import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import constants from 'src/constant.json';
import { AuthorizeService } from '../_core/access-control/authorize.service';
import { UserState } from '../_shared/state/user.state';
import { ValidatorsService } from '../shared/service/validators.service';
import { CmaService } from './cma.service';
import { CmaState } from './cma.state';

@Component({
  selector: 'app-cma',
  templateUrl: './cma.component.html',
  styleUrls: ['./cma.component.scss'],
})
export class CmaComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  reportCards: any[] = [];
  pageSize = 9;
  totalReports = 0;
  currentPage = 0;
  isLoadingReports: boolean = false;
  isInvestor: boolean = false;
  onDestroyNotifier$ = new Subject();
  defaultParams = {
    offset: 0,
    limit: 9,
    pageIndex: 0,
  };
  reportType: any;
  companyAlias: any;
  constructor(
    public cmaState: CmaState,
    public cmaService: CmaService,
    private cdr: ChangeDetectorRef,
    private userState: UserState,
    private route: ActivatedRoute,
    private authorizeService: AuthorizeService,
    public appState: AppState,
    private subscriptionService: SubscriptionPlanService,
    public breakpointService: ValidatorsService,
    public _pktrAppMenuService: PktrAppMenuService,
  ) { }

  ngOnInit(): void {
      this.route.queryParamMap.subscribe((params) => {
      if(params.get('city')){
        this.cmaState.hideReportLoader = true;
        this.openCreateReportSheet()
      }
    });
    const reportCategory = this.route.snapshot.queryParamMap.get('reportCategory');
    this.reportType = this.appState.companyInfoValue.appConfig?.cmaReport?.displayName ? this.appState.companyInfoValue.appConfig.cmaReport.displayName : 'CMA';
    const status = this.route.snapshot.queryParamMap.get('status');
    this.companyAlias = this.appState.companyInfoValue.alias;
    if (reportCategory || status) {
      this.defaultParams['reportCategory'] = reportCategory;
      const statusArray = Array.isArray(status) ? status : [status];
      this.defaultParams['status'] = statusArray;
      this.cmaState.filterValValue.reportCategory = reportCategory;
      this.cmaState.filterValValue.status = statusArray;

      this.getReportsProperties(this.defaultParams);
    } else {
      this.getReportsProperties(this.cmaState.filterValValue);
    }

    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        if (res?.roles?.length > 0) {
          let userRole = res.roles[0];

          if (userRole == 'investor') {
            this.isInvestor = true;
          } else {
            this.isInvestor = false;
          }
        }
      }
    });

    this.cmaState.showPropertyReports$.subscribe((res: any) => {
      if (res) {
        this.reportCards = res.paginatedResults || [];
        this.totalReports = res.totalRecords;
      } else {
        this.reportCards = [];
        this.totalReports = 0;
      }
      this.cdr.detectChanges();
    });
  }

  reportDeleted(reportId: string) {

    this.cmaService.deleteReport(reportId)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe(({ isLoading, data }) => {
        if (data) {
          this.cmaState.filterValValue.currentPageSize =
            this.cmaState.filterValValue.currentPageSize > 0
              ? this.cmaState.filterValValue.currentPageSize - 1
              : this.cmaState.filterValValue.currentPageSize;

          if (this.cmaState.filterValValue.currentPageSize == 0 && this.cmaState.filterValValue.offset > 0) {
            this.cmaState.filterValValue.offset = this.cmaState.filterValValue.offset - this.cmaState.filterValValue.limit;
            this.cmaState.filterValValue.pageIndex = this.cmaState.filterValValue.pageIndex - 1;
          }

          this.totalReports = this.totalReports - 1;

          this.currentPage = this.cmaState.filterValValue.pageIndex;
          this.paginator.page.next({
            pageIndex: this.cmaState.filterValValue.pageIndex,
            pageSize: this.pageSize,
            length: this.totalReports,
          });
        }
      });
  }

  refreshComponent() {
    this.getReportsProperties(this.cmaState.filterValValue);
  }

  openCreateReportSheet() {
    console.log('entered create smaet');
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        console.log('isMobileOrTablet', isMobileOrTablet);
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }
      });

    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.createCma.key)) return;

    if (!this.authorizeService.canAct('cmaReportCreate')) {
      return false;
    }
    this.cmaState.openCreateReportSheetValue = true;
    this.cdr.detectChanges();
  }

  filterChange(event) {
    this.isLoadingReports = true;
    this.currentPage = 0;
    this.cmaState.filterValValue = {
      offset: 0,
      limit: this.pageSize,
      pageIndex: 0,
      searchAddress: event.searchAddress,
      ...(event.reportCategory !== 'all' && { reportCategory: event.reportCategory }),
      ...(event.status !== 'all' && { status: [event.status] }),
      ...(event.coBrandedWithMe && { coBrandedWithMe: event.coBrandedWithMe }),
    };
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.getReportsProperties(this.cmaState.filterValValue);
  }

  getReportsProperties(param) {
    const updatedParam = { ...param };
    if (updatedParam.status && !Array.isArray(updatedParam.status)) {
      updatedParam.status = [updatedParam.status]; // wrap in array
    }
    this.cmaService.getAllCmaReports(updatedParam).subscribe(success => {
      if (success) {
        this.paginator.pageIndex = this.cmaState.filterValValue?.pageIndex || 0;
      } else {
        console.log('Failed to load reports');
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.cmaState.filterValValue.offset = this.currentPage * this.cmaState.filterValValue.limit;
    this.cmaState.filterValValue.pageIndex = event.pageIndex;
    this.getReportsProperties(this.cmaState.filterValValue);
  }

}
